import { t } from "../i18n";
import { currentRoute, Link, usePath } from "../lib/router";
import { SITE_NAME, SLOW, STREAM_BPM, streamHref, switchStream } from "../stream";
import { Avatar } from "./Avatar";
import { Logo } from "./Logo";
import { SKINS, useSettings } from "./settings";
import { useSession } from "../session";
import { TourMenu } from "../tour/TourLaunch";

// "Create" is the calendar: songs are written for a day picked there (the editor opens from it and
// keeps the tab marked). `also` lists other routes that belong to a tab.
const NAV: { href: string; label: () => string; also?: string[] }[] = [
  { href: "/", label: () => t("nav.river") },
  { href: "/calendar", label: () => t("nav.create"), also: ["/write"] },
  { href: "/fame", label: () => t("nav.fame") },
  { href: "/mixtapes", label: () => t("nav.mixtapes") },
  { href: "/tapes", label: () => t("nav.tapes") },
];

export function Header() {
  const path = usePath();
  const { skin, setSkin } = useSettings();
  const user = useSession((s) => s.user);
  return (
    <header className="site-header">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Link href="/" className="brand" aria-label={`${SITE_NAME} — home`}>
        <Logo size={26} />
        <span className="brand-name">
          {t("app.name")}
          {SLOW && <span className="brand-72"> 72</span>}
        </span>
        <span className="brand-sep" aria-hidden="true" />
        <span className="brand-tag">{t("app.subtitle")}</span>
      </Link>
      <TempoSwitch />
      <nav aria-label="Main" className="main-nav">
        {NAV.map((n) => (
          <Link
            key={n.href}
            href={n.href}
            className="nav-link"
            aria-current={
              [n.href, ...(n.also ?? [])].some((h) => path === h || (h !== "/" && path.startsWith(`${h}/`)))
                ? "page"
                : undefined
            }
          >
            {n.label()}
          </Link>
        ))}
      </nav>
      <div className="header-tools">
        <TourMenu />
        <div className="segmented" role="radiogroup" aria-label={t("skin.label")}>
          {SKINS.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={skin === s}
              className={skin === s ? "on" : ""}
              onClick={() => setSkin(s)}
            >
              {t(`skin.${s}`)}
            </button>
          ))}
        </div>
        {user ? (
          <span className="row header-user">
            {user.nickname && (
              <Link
                href={`/u/${encodeURIComponent(user.nickname)}`}
                aria-label="Your profile"
                className="header-avatar"
              >
                <Avatar nickname={user.nickname} size={30} />
              </Link>
            )}
            <Link href="/account" className="nav-link" data-testid="account-link">
              {user.nickname ?? t("nav.account")}
            </Link>
          </span>
        ) : (
          <>
            <Link href="/signin" className="nav-link" data-testid="signin-link">
              {t("nav.signin")}
            </Link>
            <Link href="/join" className="btn btn-small btn-primary" data-testid="join-link">
              {t("nav.join")}
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

/** The one-button switch between the two sister sites: The Infinite (125 BPM) and The Infinite
 *  72 (72 BPM). Same account, likes and tapes; the other site opens on the same page where it
 *  has one. */
function TempoSwitch() {
  const other = SLOW ? 125 : 72;
  return (
    <a
      href={streamHref(other, "/")}
      className={`tempo-switch${SLOW ? " slow" : ""}`}
      onClick={(e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        switchStream(currentRoute());
      }}
      aria-label={`Tempo ${STREAM_BPM} BPM — switch to the ${other} BPM site`}
      title={SLOW ? "Back to The Infinite at 125 BPM" : "The Infinite 72: slow jams and double-time trap at 72 BPM"}
      data-testid="tempo-switch"
    >
      <span className={`tempo-pos${SLOW ? "" : " on"}`}>125</span>
      <span className="tempo-knob" aria-hidden="true" />
      <span className={`tempo-pos${SLOW ? " on" : ""}`}>72</span>
      <span className="tempo-unit">BPM</span>
    </a>
  );
}
